<template>
    <div id="playlist-editor" class="max-w-screen w-full h-full max-h-full min-h-0 flex flex-col">
        <NotebookPagesEditor :name="playlist?.name || 'Notebook'" v-model="pages" 
            titlePlaceholder="Song's title" 
            subtitlePlaceholder="Artist"
            pagePlaceholder="Write the lyric here or paste a link..."
            headerClasses="text-2xl font-bold"
            :loading="loading"
            :canAddPage="playlist?._id"
            @save:page="onManualSave" 
            @update:modelValue="onUpdateModelValue"
            @update:name="onUpdateName"
            @back="onBack"
            @delete:page="onDeletePage"
            @goto:screen="gotoScreen"
            @create:version="onCreateVersion"
            @show:versions="onShowVersions"
            @addSongToFavorite="onAddSongToFavorite"
            @transpose="onTransposePage" />
        
        <SongVersionsModal 
            ref="versionsModal"
            :songId="selectedSongForVersions?.songId"
            :songTitle="selectedSongForVersions?.title"
            :currentContent="selectedSongForVersions?.content"
            :currentVersionId="selectedSongForVersions?.appliedVersionId"
            @select="onVersionSelected"
            @close="onVersionsModalClose" />
    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, defineProps, defineEmits, defineExpose, toRaw, watch, nextTick } from 'vue';
import NotebookPagesEditor from '@/components/common/NotebookPagesEditor.vue';
import SongVersionsModal from '@/components/playlist/SongVersionsModal.vue';
import toastStore from '@/store/toast.store.js'
import appStore from '@/store/app.store.js'
import userStore from '@/store/user.store.js'
import { useRouter } from 'vue-router';
import { realtimeService } from '@/service/realtime.service'
import { debounce } from '@/service/debounce.js'
import songService from '@/service/song.service.js'
import { appEvents } from '@/store/app.events.js'
import dialogStore from '@/store/dialog.store.js'

const playlist = ref({
    _id: null,
    name: '',
    songs: []
})
const pages = ref([])
const router = useRouter();
const loading = ref(true)
const isSaving = ref(false)
const versionsModal = ref(null)
const selectedSongForVersions = ref(null)
const props = defineProps({
    playlistId: {
        type: String,
        required: false
    },
    autoSave: {
        type: Boolean,
        default: true
    }
})

const emit = defineEmits(['update:playlist', 'save:playlist', 'saving:playlist'])
onMounted(async () => {
    if (props.playlistId) { 
        init();
        await fetchPlaylist(props.playlistId);
        lastSavedSignature = getPlaylistSignature();
    } else {
        loading.value = false
    }
    appEvents.on('playlistUpdated', onPlaylistUpdated);
});

onUnmounted(() => {
    appEvents.off('playlistUpdated', onPlaylistUpdated);
    if (debouncedAutoSave && typeof debouncedAutoSave.cancel === 'function') {
        debouncedAutoSave.cancel();
    }
});

const onPlaylistUpdated = async (playlistId) => {
    if (props.playlistId === playlistId && !isSaving.value) {
        const hasModifiedPages = pages.value.some(p => p.isModified)
        if (!hasModifiedPages) {
            await fetchPlaylist(playlistId);
        }
    }
}

watch(() => props.playlistId, async (newVal) => {
    if (newVal) {
        await fetchPlaylist(newVal);
    }
});

const fetchPlaylist = async (playlistId) => {
    try{
        const response = await fetch(`/api/playlist/${playlistId}?apply_transpose=false`);

        if (response.status === 200) {
            const data = await response.json()
            playlist.value = data;
            emit('update:playlist', data);
            const convertedPages = data.songs.map(song => convertSongToPage(song));
            
            // Transpose initial page contents if page.chord is non-zero
            for (let p of convertedPages) {
                if (p.chord && p.chord !== 0 && p.content) {
                    try {
                        const tResp = await fetch('/api/song/transpose-html', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ html: p.content, semitone: p.chord })
                        });
                        if (tResp.ok) {
                            const tData = await tResp.json();
                            if (tData.html) {
                                p.content = tData.html;
                            }
                        }
                    } catch (e) {
                        console.error('Error transposing page on fetchPlaylist:', e);
                    }
                }
            }

            pages.value = convertedPages;
            hydrateVersionNames(pages.value);
        } else {
            toastStore.dispatch('addMessage', {
                message: 'Error fetching playlist',
                icon: 'error',
                ttl: 3000
            })
        }   
    } catch (error) {
        console.error('Error fetching playlist:', error)
        toastStore.dispatch('addMessage', {
            message: 'Error fetching playlist',
            icon: 'error',
            ttl: 3000
        })
    }finally{
        loading.value = false
    }
}

const hydrateVersionNames = async (pagesArray) => {
    let changed = false;
    for (let page of pagesArray) {
        if (page.appliedVersionId && (!page.appliedVersionName || page.appliedVersionName === 'Version Selected')) {
            try {
                const songId = page._id || page.metadata?._id;
                if (!songId) continue;
                const response = await songService.fetchSongVersion(songId, page.appliedVersionId);
                if (response.ok) {
                    const versionData = await response.json();
                    if (versionData && versionData.name) {
                        page.appliedVersionName = versionData.name;
                        changed = true;
                    }
                }
            } catch (e) {
                console.error("Error fetching version name:", e);
            }
        }
    }
    if (changed) {
        pages.value = [...pagesArray];
    }
}

const NIL_UUID = '00000000-0000-0000-0000-000000000000'

let lastSavedSignature = ''

const saveQueues = {};
const enqueueSave = (pageId, saveFn) => {
    if (!saveQueues[pageId]) {
        saveQueues[pageId] = Promise.resolve();
    }
    saveQueues[pageId] = saveQueues[pageId].then(async () => {
        try {
            await saveFn();
        } catch (e) {
            console.error("Queue save execution failed:", e);
        }
    });
    return saveQueues[pageId];
};

import playlistService from '@/service/playlist.service.js'

const invalidateCache = (songId) => {
    if (window.cacheInstance && props.playlistId) {
        const playlistKey = playlistService.PLAYLIST_CACHE_KEY.replace('[id]', props.playlistId);
        window.cacheInstance.removeItem(playlistKey);
        if (songId) {
            const songKey = playlistService.PLAYLIST_SONG_CACHE_KEY
                .replace('[id]', props.playlistId)
                .replace('[songId]', songId);
            window.cacheInstance.removeItem(songKey);
        }
    }
};

const getPlaylistSignature = () => {
    return JSON.stringify({
        name: playlist.value.name,
        songs: playlist.value.songs.map(s => ({
            song: s.song,
            order: s.order,
            type: s.type,
            title: s.data?.title,
            subtitle: s.data?.artist?.name
        }))
    })
}

const isValidId = (id) => id && id !== NIL_UUID

const convertSongToPage = (song) => {
    const songId = song.song || song.songId || song.data?.id || song.data?._id || null
    const artist = song.data?.artist || song.artist || null
    const chordSemitone = Number(song.chord || 0)

    const songVersionId = song.songVersionId || song.data?.songVersionId || null
    const songVersionName = song.songVersion?.name || song.data?.songVersion?.name || song.songVersionName || song.data?.songVersionName || (songVersionId ? 'Version Selected' : null)
    return {
        _id: songId,
        type: song.type || "SONG",
        title: removeHtmlTags(song.data?.title || ''),
        subtitle: removeHtmlTags(artist?.name || ''),
        artist: artist ? { id: artist.id || artist._id, name: artist.name } : null,
        content: song.modifiedLyric || song.data?.content || song.data?.lyric || '',
        mediaLink: getMediaLinkHtml(song.data?.media) || '',
        tags: (song.data?.tags || song.tags || []).map(t => typeof t === 'string' ? { id: t, name: t } : t),
        isPublic: song.data?.isPublic || false,
        isModified: false,
        userAdded: !songId,
        appliedVersionId: songVersionId,
        appliedVersionName: songVersionName,
        chord: chordSemitone,
        metadata: {
            _id: songId,
            type: song.type || "SONG",
            originalTitle: removeHtmlTags(song.data?.title || ''),
            originalSubtitle: removeHtmlTags(artist?.name || ''),
            originalArtistId: artist ? (artist.id || artist._id) : null,
            originalMedia: getMediaLinkHtml(song.data?.media) || '',
            originalContent: song.modifiedLyric || song.data?.content || song.data?.lyric || '',
            originalTags: JSON.parse(JSON.stringify((song.data?.tags || song.tags || []).map(t => typeof t === 'string' ? { id: t, name: t } : t))),
            originalChord: chordSemitone,
            songVersionId: songVersionId
        }
    }
}

const removeHtmlTags = (text) => {
    return text.replace(/<[^>]*>?/g, '');
}

const getMediaLinkHtml = (mediaLink) => {
    if (!mediaLink) {
        return null
    }
    if (Array.isArray(mediaLink)) {
        if (mediaLink.length > 0) {
            return mediaLink[0]?.preview || mediaLink[0]
        } else {
            return null
        }
    } else {
        return mediaLink?.preview
    }
}
const defineType = (mediaLink) => {
    if (!mediaLink) {
        return 'audio'
    }
    if (mediaLink.includes('youtube') || mediaLink.includes('youtu.be')) {
        return 'youtube'
    } else if (mediaLink.includes('spotify')) {
        return 'spotify'
    } else {
        return 'audio'
    }
}

const convertPageToSong = (page, idx) => {
    const artistData = page.artist?.id 
        ? { id: page.artist.id, name: page.artist.name } 
        : (page.artist?.name 
            ? { name: page.artist.name } 
            : (page.subtitle ? { name: page.subtitle } : undefined))

    const metadataChanged = {
        title: page.title,
        artist: artistData,
        media: page.mediaLink ? [page.mediaLink] : [],
        tags: (page.tags || []).map(t => typeof t === 'object' ? t.name : t)
    }

    return {
        type: page.metadata?.type || "SONG",
        userAdded: page.userAdded || !page._id,
        song: page.metadata?._id || null,
        order: page.order || idx + 1,
        modifiedLyric: page.content,
        isModified: page.isModified,
        songMetadataChanged: metadataChanged,
        song_metadata_changed: metadataChanged,
        data: {
            title: page.title,
            artist: artistData,
            content: page.content || null,
            isPublic: page.isPublic || false,
            media: page.mediaLink ? [page.mediaLink] : [],
            tags: (page.tags || []).map(t => typeof t === 'object' ? t.name : t)
        }
    }
}

const saveNewPage = async (page, order) => {
    if (!page.title || !page.title.trim()) return null
    
    const artistName = (page.subtitle && page.subtitle.trim()) 
        ? page.subtitle.trim() 
        : (page.artist?.name || 'Various Artists');

    const artistData = page.artist?.id 
        ? { id: page.artist.id, name: artistName } 
        : (page.artist?.name 
            ? { name: page.artist.name } 
            : { name: artistName })
    
    try {
        const response = await songService.addPageToPlaylist(props.playlistId, {
            title: page.title.trim(),
            artist: artistData,
            content: page.content || undefined,
            order: order,
            is_public: page.isPublic || false,
            tags: (page.tags || []).map(t => typeof t === 'object' ? t.name : t)
        })
        if (response.ok) {
            const song = await response.json()
            const songId = song.id || song._id
            invalidateCache(songId)
            return { songId, song }
        } else {
            console.error("saveNewPage API error! Status:", response.status, "Body:", await response.text());
        }
    } catch (e) {
        console.error('Error adding page to playlist:', e)
    }
    return null
}

// Save individual page changes: creates new or updates content
const autoSavePage = async (page, index) => {
    if (!page.title || !page.title.trim()) return
    
    if (!page.metadata?._id) {
        // NEW page — auto creates with title and optional artist/subtitle
        const result = await saveNewPage(page, index + 1)
        if (result) {
            page._id = result.songId
            page.metadata = { 
                _id: result.songId, 
                type: "SONG",
                originalTitle: page.title,
                originalSubtitle: page.subtitle,
                originalArtistId: page.artist?.id || null,
                originalMedia: page.mediaLink || '',
                originalContent: page.content || '',
                originalTags: JSON.parse(JSON.stringify(page.tags || []))
            }
            if (result.song.artist) {
                page.artist = { 
                    id: result.song.artist.id || result.song.artist._id, 
                    name: result.song.artist.name 
                }
                page.subtitle = result.song.artist.name
            } else if (result.song.artist_id && !page.artist?.id) {
                page.artist = { id: result.song.artist_id, name: page.subtitle }
            }
            
            // Handle version ID from result if we saved with one
            if (result.song.songVersionId) {
                page.appliedVersionId = result.song.songVersionId;
                page.metadata.songVersionId = result.song.songVersionId;
            }
            // Update the actual page in the current array in case it was replaced by syncOut
            const currentPage = pages.value.find(p => p.uid === page.uid)
            if (currentPage && currentPage !== page) {
                currentPage._id = page._id
                currentPage.metadata = page.metadata
                currentPage.artist = page.artist
                currentPage.subtitle = page.subtitle
            }
            page.isModified = false;
        }
    } else {
        // EXISTING page
        const pageId = page.metadata._id;
        page.isModified = false; // Clear immediately so subsequent edits mark as modified again

        await enqueueSave(pageId, async () => {
            const originalTitle = page.metadata?.originalTitle || '';
            const originalSubtitle = page.metadata?.originalSubtitle || '';
            const originalArtistId = page.metadata?.originalArtistId || null;
            const originalMedia = page.metadata?.originalMedia || '';
            const originalTags = page.metadata?.originalTags || [];
            const originalContent = page.metadata?.originalContent || '';
            const originalVersionId = page.metadata?.songVersionId || null;
            const originalChord = page.metadata?.originalChord || 0;
            
            const titleChanged = page.title !== originalTitle;
            const subtitleChanged = page.subtitle !== originalSubtitle;
            const artistIdChanged = (page.artist?.id || null) !== originalArtistId;
            const mediaChanged = page.mediaLink !== originalMedia;
            const tagsChanged = JSON.stringify(page.tags || []) !== JSON.stringify(originalTags);
            const contentChanged = page.content && page.content !== originalContent;
            const versionChanged = page.appliedVersionId !== originalVersionId;
            const chordChanged = (page.chord || 0) !== originalChord;

            if (titleChanged || subtitleChanged || artistIdChanged || mediaChanged || contentChanged || versionChanged || tagsChanged || chordChanged) {
                const payload = {
                    songMetadataChanged: {
                        title: page.title,
                        artist: page.artist?.id ? { id: page.artist.id, name: page.artist.name } : { name: page.subtitle },
                        media: page.mediaLink ? [page.mediaLink] : [],
                        tags: (page.tags || []).map(t => typeof t === 'object' ? t.name : t)
                    }
                };
                if (contentChanged || chordChanged) {
                    payload.content = page.content;
                    if (page.chord && page.chord !== 0) {
                        payload.reverse_transpose = -page.chord;
                    }
                }
                if (versionChanged) {
                    const versionIdToSend = page.appliedVersionId || page.metadata?.songVersionId;
                    if (versionIdToSend) {
                        payload.songVersionId = versionIdToSend;
                    }
                }

                try {
                    await songService.savePlaylistSong(props.playlistId, pageId, payload);
                    if (chordChanged) {
                        try {
                            await fetch(`/api/playlist/${props.playlistId}/song/transpose/${pageId}?semitone=${page.chord || 0}`);
                        } catch (te) {
                            console.error('Error saving song transpose:', te);
                        }
                    }
                    invalidateCache(pageId);
                    if (!page.metadata) page.metadata = {};
                    page.metadata.originalTitle = page.title;
                    page.metadata.originalSubtitle = page.subtitle;
                    page.metadata.originalArtistId = page.artist?.id || null;
                    page.metadata.originalMedia = page.mediaLink || '';
                    page.metadata.originalContent = page.content;
                    page.metadata.originalTags = JSON.parse(JSON.stringify(page.tags || []));
                    page.metadata.originalChord = page.chord || 0;
                    page.metadata.songVersionId = page.appliedVersionId || null;
                } catch (e) {
                    console.error('Error updating playlist song:', e);
                    page.isModified = true; // Mark as modified again so it gets retried
                }
            }
        });
    }
}

const onTransposePage = async ({ pageIndex, page, event }) => {
    const targetPage = (pages.value && pages.value[pageIndex]) || page;
    if (!targetPage || !event) return;
    const semitone = Number(event.semitone || 0);
    const delta = Number(event.delta || 0);

    if (!targetPage.content) {
        targetPage.chord = semitone;
        return;
    }

    try {
        const response = await fetch('/api/song/transpose-html', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ html: targetPage.content, semitone: delta })
        });
        if (response.ok) {
            const data = await response.json();
            if (data.html) {
                targetPage.content = data.html;
                targetPage.chord = semitone;
                targetPage.isModified = true;
            }
        }
    } catch (e) {
        console.error('Error in onTransposePage:', e);
    }
};

const deletedPages = ref([]);

const onDeletePage = async (pageId) => {
    if (props.autoSave) {
        try {
            const response = await fetch(`/api/playlist/${props.playlistId}/song/${pageId}`, {
                method: 'DELETE'
            });
            if (response.ok) {
                invalidateCache(pageId)
            } else {
                console.error("Failed to delete song", response);
            }
        } catch (e) {
            console.error("Error deleting song", e);
        }
    } else {
        deletedPages.value.push(pageId);
    }
}

let pendingSave = false;

// Called by save routines to persist individual modified pages
const autoSaveChanges = async () => {
    // Only auto-save individual pages if the playlist already exists.
    // If it's a new playlist, they will be saved when the playlist is created.
    if (!props.playlistId) return;
    
    for (let i = 0; i < pages.value.length; i++) {
        const page = pages.value[i]
        if (page.isModified) {
            await autoSavePage(page, i)
        }
    }
}

// Manual save — persists unsaved pages individually, handles deletions, and sorting
const onManualSave = async (showToasts = true) => {
    if (isSaving.value) {
        pendingSave = true;
        return false;
    }

    // Validate all pages before saving
    for (let i = 0; i < pages.value.length; i++) {
        const p = pages.value[i];
        const pageNum = i + 1;
        
        if (!p.title || !p.title.trim()) {
            if (showToasts) {
                toastStore.dispatch('addMessage', {
                    message: `Page ${pageNum} is missing a title.`,
                    icon: 'error',
                    ttl: 5000
                });
            }
            return false;
        }
    }

    isSaving.value = true;
    emit('saving:playlist');
    try {
        await autoSaveChanges() // creates/updates modified songs

        // Handle deferred deletions if autoSave was off
        for (const pageId of deletedPages.value) {
             try {
                const response = await fetch(`/api/playlist/${props.playlistId}/song/${pageId}`, { method: 'DELETE' });
                if (response.ok) {
                    invalidateCache(pageId);
                }
             } catch(e) {
                  console.error(e);
             }
        }
        deletedPages.value = [];

        const songs = pages.value.map((p, idx) => convertPageToSong(p, idx))
        playlist.value.songs = songs
        const p = {
            _id: props.playlistId || playlist.value._id,
            id: props.playlistId || playlist.value._id,
            name: playlist.value.name
            // We omit `songs` here since we do not want to bulk PUT them anymore
        }

        const currentSignature = getPlaylistSignature()
        if (currentSignature !== lastSavedSignature && props.playlistId) {
            // Sort changed? Let's always send sort just in case, it's cheap
            const sortData = songs
                .map(s => ({ song: s.song || s.data?.id || s.data?._id, order: s.order }))
                .filter(s => isValidId(s.song));
            
            if (sortData.length > 0) {
                try {
                    await fetch(`/api/playlist/${props.playlistId}/song/sort`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(sortData)
                    });
                } catch(e) {
                    console.error("Error sorting", e);
                }
            }
        }

        let originalPlaylistName = '';
        try {
            const lastSigObj = JSON.parse(lastSavedSignature);
            originalPlaylistName = lastSigObj.name || '';
        } catch(e) {
            console.error("Failed to parse lastSavedSignature:", e);
        }
        
        const hasUnsavedPages = pages.value.some(page => !page.metadata?._id);
        const skipPut = playlist.value.name === originalPlaylistName && !hasUnsavedPages;

        return new Promise((resolve) => {
            // Emits save:playlist ONLY with metadata (name), without songs array
            emit('save:playlist', toRaw(p), () => {
                isSaving.value = false;
                lastSavedSignature = getPlaylistSignature();
                if (pendingSave) {
                    pendingSave = false;
                    debouncedAutoSave();
                }
                resolve(true);
            }, skipPut)
        });
    } catch (error) {
        isSaving.value = false;
        console.error('Save failed', error);
        return false;
    }
}

// Auto-save debounce — reads latest pages.value at execution time
const debouncedAutoSave = debounce(() => {
    if (!props.autoSave) return;
    const currentSignature = getPlaylistSignature()
    const hasModifiedPages = pages.value.some(p => p.isModified || (!p.metadata?._id && p.title && p.title.trim()))

    if (currentSignature !== lastSavedSignature || hasModifiedPages) {
        onManualSave(false)
    }
}, 1500)

const onUpdateModelValue = (p) => {
    pages.value = p
    playlist.value.songs = pages.value.map((p, idx) => convertPageToSong(p, idx))
    
    debouncedAutoSave()

    emit('update:playlist', { ...playlist.value, _id: props.playlistId || playlist.value._id, id: props.playlistId || playlist.value._id })
}

const onUpdateName = (name) => {
    playlist.value.name = name
         // Auto-save on name change
         debouncedAutoSave()
}
const onBack = () => {
    // The NotebookPagesEditor component handles the unsaved changes check
    // Just emit the back event
    router.push({ path: `/playlist/${props.playlistId}` });
}
appStore.watch((state) => state.transferBucket, async (newVal) => {
    if (newVal) {
        if (pages.value.some(p => p._id === newVal._id)) {
            toastStore.dispatch('addMessage', {
                message: 'Song already in playlist',
                icon: 'error',
                ttl: 5000
            });
            return;
        } else {
            let pageData = newVal;
            if (newVal._id) {
                try {
                    const response = await songService.fetchSong(newVal._id);
                    if (response.ok) {
                        pageData = await response.json();
                    }
                } catch (e) {
                    console.error("Failed to fetch full song", e);
                }
            } else {
                const processedLyric = await processLyric(newVal.lyric);
                pageData.lyric = processedLyric;
            }

            const page = convertSongToPage({
                data: pageData
            })
            
            // Explicitly add existing song to playlist since autoSave ignores unmodified existing pages
            if (pageData._id && props.playlistId) {
                try {
                    await songService.add(props.playlistId, pageData._id);
                    invalidateCache(pageData._id);
                } catch (e) {
                    console.error("Failed to add song to playlist", e);
                }
            }

            pages.value.push(page)
            toastStore.dispatch('addMessage', {
                message: 'Transferred to screen: ' + pageData.title,
                icon: 'success',
                ttl: 5000
            });
            // Auto-save on song added through transfer area
            onManualSave()
        }
    }
});

const processLyric = async (lyric) => {
    const response = await fetch('/api/song/process', {
        method: 'POST',
        body: JSON.stringify({ lyric: lyric }),
        headers: {
            'Content-Type': 'application/json'
        }
    })
    if (response.status === 200) {
        const data = await response.json()
        return data.preview
    } else {
        return lyric
    }
}
const gotoScreen = (songId) => {
    router.push({
        path: `/playlist/${props.playlistId}/song/${songId}`
    })
}

const onAddSongToFavorite = async (eventData) => {
    const { songId, title, appliedVersionId } = eventData
    if (!songId) {
        toastStore.dispatch('addMessage', {
            message: 'Cannot favorite an unsaved song. Please save it first.',
            icon: 'error',
            ttl: 5000
        })
        return
    }

    try {
        const response = await songService.addFavoriteSong(songId, appliedVersionId || null)
        if (response.ok) {
            toastStore.dispatch('addMessage', {
                message: `"${title}" added to favorites!`,
                icon: 'success',
                ttl: 3000
            })
        } else {
            const errorData = await response.json()
            if (errorData.error === 'LIMIT_REACHED') {
                toastStore.dispatch('addMessage', {
                    message: `Limit reached: You can only have ${errorData.limit} favorite songs.`,
                    icon: 'error',
                    ttl: 5000
                })
            } else {
                toastStore.dispatch('addMessage', {
                    message: errorData.message || 'Error adding song to favorites',
                    icon: 'error',
                    ttl: 5000
                })
            }
        }
    } catch (error) {
        console.error('Error adding favorite:', error)
        toastStore.dispatch('addMessage', {
            message: 'Error adding favorite',
            icon: 'error',
            ttl: 5000
        })
    }
}

const onCreateVersion = async (versionData) => {
    const { songId, content, mediaLink } = versionData
    
    if (!songId) {
        toastStore.dispatch('addMessage', {
            message: 'Cannot create version for unsaved song. Please save the song first.',
            icon: 'error',
            ttl: 5000
        })
        return
    }

    // Generate a version name based on current timestamp
    const now = new Date()
    const versionName = `Version ${now.toLocaleDateString()} ${now.toLocaleTimeString()}`
    
    try {
        const response = await songService.createSongVersion(songId, {
            name: versionName,
            content: content,
            chord: null,
            media: mediaLink ? [{ type: defineType(mediaLink), name: '', preview: mediaLink }] : [],
            is_public: false
        })
        
        if (response.ok) {
            toastStore.dispatch('addMessage', {
                message: `Version "${versionName}" created successfully`,
                icon: 'success',
                ttl: 3000
            })
        } else {
            const errorData = await response.json()
            toastStore.dispatch('addMessage', {
                message: errorData.message || 'Error creating version',
                icon: 'error',
                ttl: 5000
            })
        }
    } catch (error) {
        console.error('Error creating version:', error)
        toastStore.dispatch('addMessage', {
            message: 'Error creating version',
            icon: 'error',
            ttl: 5000
        })
    }
}

const onShowVersions = (versionData) => {
    const { songId, title, content, appliedVersionId } = versionData
    
    if (!songId) {
        toastStore.dispatch('addMessage', {
            message: 'Cannot show versions for unsaved song. Please save the song first.',
            icon: 'error',
            ttl: 5000
        })
        return
    }
    
    selectedSongForVersions.value = { songId, title, content, appliedVersionId: appliedVersionId || null }
    nextTick(() => {
        if (versionsModal.value && typeof versionsModal.value.open === 'function') {
            versionsModal.value.open()
        }
    })
}

const onVersionSelected = async (versionData) => {
    const { versionId, content, name, media, artist, title } = versionData
    
    if (!selectedSongForVersions.value) {
        return
    }
    
    const { songId } = selectedSongForVersions.value
    
    // Find the page in the pages array and update its content
    const pageIndex = pages.value.findIndex(p => 
        (p._id === songId || p.metadata?._id === songId)
    )
    
    if (pageIndex !== -1) {
        // Create a new pages array to trigger reactivity
        const updatedPages = [...pages.value]
        updatedPages[pageIndex] = {
            ...updatedPages[pageIndex],
            content: content,
            isModified: true,
            appliedVersionId: versionId,
            appliedVersionName: name,
            ...(media !== undefined && { mediaLink: getMediaLinkHtml(media) || '' }),
            ...(artist !== undefined && { artist: artist ? { id: artist.id || artist._id, name: artist.name } : null, subtitle: artist?.name || '' }),
            ...(title !== undefined && { title: title || '' })
        }
        
        // Update the pages array
        pages.value = updatedPages
        
        // Save immediately — version selection is an explicit action,
        // must persist before any reactive reload can overwrite the page
        await autoSavePage(pages.value[pageIndex], pageIndex)
        
        toastStore.dispatch('addMessage', {
            message: `Applied version "${name}" to the song`,
            icon: 'success',
            ttl: 3000
        })
    }
}

const onVersionsModalClose = () => {
    selectedSongForVersions.value = null
}

// Method to update pages from playlist (called after successful save)
const updatePagesFromPlaylist = (updatedPlaylist) => {
    if (updatedPlaylist && updatedPlaylist.songs) {
        if (pages.value.length === 0 || !updatedPlaylist._id) {
            const newPages = updatedPlaylist.songs.map((song, idx) => {
                const page = convertSongToPage(song)
                page.order = idx + 1;
                return page
            })
            pages.value = newPages
            hydrateVersionNames(pages.value)
        } else {
            updatedPlaylist.songs.forEach((song, idx) => {
                if (pages.value[idx]) {
                    const rawSongId = song.data?.id || song.data?._id
                    const songId = isValidId(rawSongId) ? rawSongId : null
                    if (!pages.value[idx]._id && songId) {
                        pages.value[idx]._id = songId;
                        if (!pages.value[idx].metadata) {
                            pages.value[idx].metadata = {};
                        }
                        pages.value[idx].metadata._id = songId;
                        pages.value[idx].metadata.type = "SONG";
                    }
                    if (song.modifiedLyric) {
                        pages.value[idx].content = song.modifiedLyric;
                        pages.value[idx].isModified = false;
                    }
                    const songVersionId = song.songVersionId || song.data?.songVersionId || null;
                    const songVersionName = song.songVersion?.name || song.data?.songVersion?.name || song.songVersionName || song.data?.songVersionName || (songVersionId ? 'Version Selected' : null);
                    if (songVersionId) {
                        pages.value[idx].appliedVersionId = songVersionId;
                        pages.value[idx].appliedVersionName = songVersionName;
                    }
                }
            })
            hydrateVersionNames(pages.value)
        }
    }
}

const init = () => {
    realtimeService.subscribe(
        "playlist::room::"+props.playlistId,
        '*',
        (payload) => onEvent({event: payload.event, data: payload}),
        (status) => {
            if (status == 'SUBSCRIBED') { 
                console.log("playlistRoom "+props.playlistId+" subscribed successfully")
            } else {
                console.log("playlistRoom "+props.playlistId+" subscribed failed")
            }
        }
    )
}

const onEvent = async (payload) => {
    
    if (payload.event === 'update' || payload.event ==="playlist-update::"+props.playlistId) {
        const currentUserId = userStore.state.userInfo?.id || userStore.state.userInfo?._id;
        if (currentUserId && payload.data && payload.data.sender === currentUserId) {
            return;
        }
        if (window.myClientId && payload.data && payload.data.sender_client_id === window.myClientId) {
            return;
        }
        if (payload.data.realtime_user_changed) {
            if (playlist.value) {
                playlist.value.realtime_user_id = payload.data.realtime_user_id;
            }
            toastStore.dispatch('addMessage', {
                message: 'Realtime control delegated',
                icon: 'success',
                ttl: 3000
            });
            return;
        }
        if (await dialogStore.dispatch('confirm', 'Do you want to update the playlist?')) {
            await fetchPlaylist(props.playlistId);
            toastStore.dispatch('addMessage', {
                message: 'Playlist updated successfully',
                icon: 'success',
                ttl: 5000
            });
        }
    }
}

// Expose method for parent component
defineExpose({
    updatePagesFromPlaylist,
    onManualSave
})

</script>